import { useState } from "react";
import { CapAwareButton } from "../../components/CapAwareButton";

import { Layout } from "../../components/Layout";
import { PageHeader } from "../../components/PageHeader";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { formatMoney } from "../../lib/api";
import { MoneyAmount } from "../../components/MoneyAmount";
import { MoneyTotal } from "../../components/MoneyTotal";
import { INVESTMENT_ICONS, pickIcon } from "../../lib/typeIcons";
import { useInvestments, useInvestmentsMutations, useProfile, useFxConvert, useFxRates, useCashAccounts } from "../../hooks/queries";
import { useLang } from "../../contexts/LangContext";
import { tx } from "../../i18n";
import { toast } from "sonner";
import { reportValidationError } from "../../lib/validationTelemetry";
import { Plus, TrendingUp, TrendingDown, Pencil, Trash2, Archive, ArrowDownCircle } from "lucide-react";
import { pushHighlight } from "../../lib/highlights";
import { HighlightCard } from "../../components/HighlightCard";
import { api } from "../../lib/api";
import { useQueryClient } from "@tanstack/react-query";
import { Switch } from "../../components/ui/switch";
import { InvestmentFormDialog } from "./components/InvestmentFormDialog";
import { InvestmentCollectDialog } from "./components/InvestmentCollectDialog";

const KINDS = [
  { v: "stocks", labelEn: "Stocks", labelEs: "Acciones" },
  { v: "crypto", labelEn: "Crypto", labelEs: "Cripto" },
  { v: "bonds", labelEn: "Bonds", labelEs: "Bonos" },
  { v: "etf", labelEn: "ETF", labelEs: "ETF" },
  { v: "fund", labelEn: "Fund", labelEs: "Fondo" },
  { v: "dividend", labelEn: "Dividend", labelEs: "Dividendos" },
  { v: "other", labelEn: "Other", labelEs: "Otro" },
];

const CONTRIB_FREQS = [
  { v: "weekly",    es: "Semanal",     en: "Weekly" },
  { v: "biweekly",  es: "Quincenal",   en: "Biweekly" },
  { v: "monthly",   es: "Mensual",     en: "Monthly" },
  { v: "quarterly", es: "Trimestral",  en: "Quarterly" },
  { v: "yearly",    es: "Anual",       en: "Yearly" },
];

export default function Investments() {
  const { t, lang } = useLang();
  const [includeWithdrawn, setIncludeWithdrawn] = useState(false);
  const { data: items = [] } = useInvestments({ include_withdrawn: includeWithdrawn });
  const { data: profile } = useProfile();
  const currency = profile?.currency || "USD";
  const { create, update, remove: removeMut } = useInvestmentsMutations();
  const qc = useQueryClient();

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [collectTarget, setCollectTarget] = useState(null);
  const [collectAmount, setCollectAmount] = useState("");
  const [collectAccount, setCollectAccount] = useState("");
  const [collectNote, setCollectNote] = useState("");
  const [form, setForm] = useState({
    name: "", kind: "stocks", ticker: "", currency: "",
    amount_invested: "", current_value: "", purchase_date: "",
    expected_annual_return: "", withdrawal_date: "", notes: "",
    contribution_amount: "", contribution_frequency: "",
  });
  // iter-169zze · field-level error map for inline highlighting.
  const [fieldErrors, setFieldErrors] = useState({});

  const openNew = () => {
    setEditing(null);
    setFieldErrors({});
    setForm({
      name: "", kind: "stocks", ticker: "", currency: "",
      amount_invested: "", current_value: "", purchase_date: "",
      expected_annual_return: "", withdrawal_date: "", notes: "",
      contribution_amount: "", contribution_frequency: "",
      contribution_account_id: "", contribution_start_date: "",
    });
    setOpen(true);
  };
  const openEdit = (it) => {
    setEditing(it);
    setFieldErrors({});
    setForm({
      name: it.name, kind: it.kind, ticker: it.ticker || "",
      currency: it.currency || "",
      amount_invested: String(it.amount_invested), current_value: String(it.current_value),
      purchase_date: it.purchase_date ? it.purchase_date.slice(0, 10) : "",
      expected_annual_return: it.expected_annual_return ? String(it.expected_annual_return) : "",
      withdrawal_date: it.withdrawal_date ? it.withdrawal_date.slice(0, 10) : "",
      notes: it.notes || "",
      contribution_amount: it.contribution_amount ? String(it.contribution_amount) : "",
      contribution_frequency: it.contribution_frequency || "",
      contribution_account_id: it.contribution_account_id || "",
      contribution_start_date: it.contribution_start_date ? it.contribution_start_date.slice(0, 10) : "",
    });
    setOpen(true);
  };

  const save = async () => {
    const isEs = lang === "es";
    const fe = {};
    if (!form.name?.trim()) fe.name = isEs ? "El nombre es obligatorio." : "Name is required.";
    if (!form.amount_invested || Number(form.amount_invested) <= 0) {
      fe.amount_invested = isEs ? "Indica el monto invertido (mayor a 0)." : "Enter the amount invested (greater than 0).";
    }
    // DCA recurring contributions need a funding account AND a start date
    // — without them the auto-payment service can't materialise the cycle.
    if (form.contribution_frequency) {
      if (!form.contribution_account_id) {
        fe.contribution_account_id = isEs
          ? "Cuenta del aporte requerida para aportes recurrentes."
          : "Account required for recurring contributions.";
      }
      if (!form.contribution_start_date) {
        fe.contribution_start_date = isEs
          ? "Fecha del primer aporte requerida."
          : "Start date required.";
      }
    }
    if (Object.keys(fe).length) {
      setFieldErrors(fe);
      reportValidationError("inv-form", fe);
      toast.error(isEs ? "Faltan campos obligatorios." : "Missing required fields.");
      return;
    }
    setFieldErrors({});
    const payload = {
      name: form.name, kind: form.kind, ticker: form.ticker || null,
      amount_invested: Number(form.amount_invested),
      current_value: Number(form.current_value || form.amount_invested),
      currency: form.currency || currency,
      purchase_date: form.purchase_date || null,
      expected_annual_return: Number(form.expected_annual_return || 0),
      withdrawal_date: form.withdrawal_date || null,
      notes: form.notes || "",
      contribution_amount: form.contribution_amount ? Number(form.contribution_amount) : null,
      contribution_frequency: form.contribution_frequency || null,
      contribution_account_id: form.contribution_account_id || null,
      contribution_start_date: form.contribution_start_date || null,
    };
    try {
      let created;
      if (editing) {
        created = await update.mutateAsync({ id: editing.id, payload });
      } else {
        created = await create.mutateAsync(payload);
      }
      // Cobros/Pagos auto-generados pueden haber aparecido — refrescar listas.
      qc.invalidateQueries({ queryKey: ["payments"] });
      qc.invalidateQueries({ queryKey: ["payments-infinite"] });
      qc.invalidateQueries({ queryKey: ["receivables"] });
      qc.invalidateQueries({ queryKey: ["receivables-infinite"] });
      pushHighlight("investment", created?.id);
      toast.success(tx(lang, "Saved", "Guardado", "Guardado"));
      setOpen(false);
    } catch (e) { console.error("save investment failed", e); /* onError toast already fired */ }
  };

  const remove = async () => {
    if (!deleteTarget) return;
    await removeMut.mutateAsync(deleteTarget.id);
    qc.invalidateQueries({ queryKey: ["payments"] });
    qc.invalidateQueries({ queryKey: ["payments-infinite"] });
    qc.invalidateQueries({ queryKey: ["receivables"] });
    qc.invalidateQueries({ queryKey: ["receivables-infinite"] });
    setDeleteTarget(null);
    toast.success(tx(lang, "Deleted", "Eliminado", "Eliminado"));
  };

  const openCollect = (it) => {
    setCollectTarget(it);
    setCollectAmount(String(it.current_value || 0));
    setCollectAccount("");
    setCollectNote("");
  };

  const submitCollect = async () => {
    if (!collectTarget) return;
    const amount = Number(collectAmount || 0);
    if (amount <= 0) return toast.error(tx(lang, "Invalid amount", "Monto inválido", "Monto inválido"));
    try {
      await api.post(`/investments/${collectTarget.id}/collect`, {
        actual_amount: amount,
        account_id: collectAccount || null,
        note: collectNote || "",
      });
      qc.invalidateQueries({ queryKey: ["investments"] });
      qc.invalidateQueries({ queryKey: ["history"] });
      qc.invalidateQueries({ queryKey: ["movements-infinite"] });
      qc.invalidateQueries({ queryKey: ["cash-accounts"] });
      pushHighlight("investment", collectTarget.id);
      toast.success(
        tx(lang, `✓ ${collectTarget.name} collected · now in Movements`, `✓ ${collectTarget.name} cobrada · ahora en Movimientos`, `✓ ${collectTarget.name} cobrada · ahora en Movimientos`),
        { duration: 5000 },
      );
      setCollectTarget(null);
    } catch (e) {
      console.error("collect investment failed", e);
      toast.error(tx(lang, "Collect failed", "Error al cobrar", "Error al cobrar"));
    }
  };

  const fxConvert = useFxConvert();
  const { data: fxInfo } = useFxRates();
  const { data: cashAccounts = [] } = useCashAccounts();
  const fxList = Object.keys(fxInfo?.rates || {}).sort();
  // Convert every investment to the profile currency for the summary cards so
  // mixed-currency portfolios add up correctly.
  const totalInvested = items.reduce(
    (s, i) => s + fxConvert(Number(i.amount_invested || 0), i.currency || currency, currency),
    0,
  );
  const totalValue = items.reduce(
    (s, i) => s + fxConvert(Number(i.current_value || 0), i.currency || currency, currency),
    0,
  );
  const pnl = totalValue - totalInvested;
  const pnlPct = totalInvested > 0 ? (pnl / totalInvested) * 100 : 0;

  const hasMixedCurrencies = items.some((i) => i.currency && i.currency !== currency);
  const fxFetchedAt = fxInfo?.fetched_at ? new Date(fxInfo.fetched_at).toLocaleString() : null;

  const kindLabel = (v) => (KINDS.find((k) => k.v === v) || {})[tx(lang, "labelEn", "labelEs", "labelEs")] || v;

  return (
    <Layout>
      <PageHeader
        eyebrow={tx(lang, "Wealth that grows", "Patrimonio que crece", "Patrimonio que crece")}
        title={tx(lang, "Investments", "Inversiones", "Inversiones")}
        subtitle={tx(lang, `${items.length} ${items.length === 1 ? "asset" : "assets"} — track returns + recurring contributions.`, `${items.length} ${items.length === 1 ? "activo" : "activos"} — sigue tu rendimiento + aportes periódicos.`, `${items.length} ${items.length === 1 ? "activo" : "activos"} — sigue tu rendimiento + aportes periódicos.`)}
        testid="investments-header"
        resourceKey="investments_tracked"
        actions={
          <div className="flex items-center gap-3 flex-wrap">
            <label className="inline-flex items-center gap-2 text-sm text-slate-600 select-none">
              <Switch
                checked={includeWithdrawn}
                onCheckedChange={setIncludeWithdrawn}
                data-testid="toggle-show-withdrawn"
              />
              <Archive className="h-3.5 w-3.5" />
              {tx(lang, "Show withdrawn", "Mostrar cobradas", "Mostrar cobradas")}
            </label>
            <CapAwareButton
              resourceKey="investments_tracked"
              featureLabel={tx(lang, "investments", "inversiones", "investimentos")}
              isEs={lang === "es"}
              hideBadge
              data-testid="add-investment-btn"
              onClick={openNew}
              className="bg-emerald-600 hover:bg-emerald-700 rounded-xl gap-2"
            >
              <Plus className="h-4 w-4" /> {tx(lang, "New investment", "Nueva inversión", "Nueva inversión")}
            </CapAwareButton>
          </div>
        }
      />

      {/* Summary */}
      <div className="grid sm:grid-cols-3 gap-3 mb-3">
        <Card className="p-4 rounded-2xl border-slate-200">
          <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">{tx(lang, "Invested", "Invertido", "Invertido")}</div>
          <MoneyTotal
            items={items.map((i) => ({ amount: i.amount_invested || 0, currency: i.currency || currency }))}
            currency={currency}
            size="lg"
            testid="inv-total-invested"
            className="mt-1"
          />
        </Card>
        <Card className="p-4 rounded-2xl border-slate-200">
          <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">{tx(lang, "Current value", "Valor actual", "Valor actual")}</div>
          <MoneyTotal
            items={items.map((i) => ({ amount: i.current_value || 0, currency: i.currency || currency }))}
            currency={currency}
            size="lg"
            testid="inv-total-value"
            className="mt-1"
          />
        </Card>
        <Card className="p-4 rounded-2xl border-slate-200">
          <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">P&L</div>
          <div className={`font-display text-2xl font-bold tabular-nums mt-1 ${pnl >= 0 ? "text-emerald-700" : "text-red-600"}`} data-testid="inv-total-pnl">
            {pnl >= 0 ? "+" : ""}{formatMoney(pnl, currency)}
            <span className="text-sm font-medium ml-2">({pnlPct.toFixed(1)}%)</span>
          </div>
        </Card>
      </div>
      {hasMixedCurrencies && (
        <div
          data-testid="fx-notice"
          className="mb-6 px-3 py-2 text-xs text-slate-500 bg-amber-50 border border-amber-200 rounded-xl"
        >
          {tx(lang, `Totals converted to ${currency} using FX rates from`, `Totales convertidos a ${currency} usando tipo de cambio del`, `Totales convertidos a ${currency} usando tipo de cambio del`)}
          {" "}
          <span className="font-semibold">{fxFetchedAt || (tx(lang, "cache", "memoria", "memoria"))}</span>.
          {" "}
          {tx(lang, "Each investment keeps its original currency.", "Cada inversión conserva su moneda original.", "Cada inversión conserva su moneda original.")}
        </div>
      )}

      <div className="grid gap-3" data-testid="investments-list">
        {items.length === 0 && (
          <Card className="p-8 text-center text-slate-500 rounded-2xl">
            {tx(lang, "No investments yet.", "Aún no has registrado inversiones.", "Aún no has registrado inversiones.")}
          </Card>
        )}
        {items.map((it) => {
          const invCur = it.currency || currency;
          const itemPnlNative = Number(it.current_value || 0) - Number(it.amount_invested || 0);
          const itemPnl = fxConvert(itemPnlNative, invCur, currency);
          const isWithdrawn = it.status === "withdrawn";
          // For withdrawn rows, surface the expected vs actual delta — the
          // entity often discounts fees or under-delivers vs Lumfi's clean
          // compound projection, so the user can see at a glance whether
          // what they received matches what they were promised.
          const expected = Number(it.expected_value_at_maturity || 0);
          const actual = Number(it.collected_amount || 0);
          const delta = isWithdrawn && expected > 0 && actual > 0 ? actual - expected : null;
          const deltaPct = delta != null && expected > 0 ? (delta / expected) * 100 : null;
          return (
            <HighlightCard
              key={it.id}
              kind="investment"
              id={it.id}
              data-testid={`investment-${it.id}`}
              className={`p-4 sm:p-5 rounded-2xl border-slate-200 flex items-center justify-between flex-wrap gap-3 ${isWithdrawn ? "opacity-80 bg-slate-50" : ""}`}
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="h-10 w-10 rounded-full bg-emerald-50 text-emerald-700 flex items-center justify-center">
                  {(() => { const KIcon = pickIcon(INVESTMENT_ICONS, it.kind); return <KIcon className="h-5 w-5" />; })()}
                </div>
                <div className="min-w-0">
                  <div className="font-semibold text-slate-900 truncate">
                    {it.name} {it.ticker && <span className="text-xs text-slate-500">({it.ticker})</span>}
                    {isWithdrawn && (
                      <Badge data-testid={`inv-badge-withdrawn-${it.id}`} className="ml-2 bg-slate-200 text-slate-700 hover:bg-slate-200 text-[10px]">
                        {tx(lang, "Withdrawn", "Cobrada", "Cobrada")}
                      </Badge>
                    )}
                  </div>
                  <div className="text-xs text-slate-500">{kindLabel(it.kind)}</div>
                  {it.contribution_amount > 0 && it.contribution_frequency && (
                    <div className="text-[11px] text-amber-700 mt-0.5 tabular-nums" data-testid={`inv-contrib-${it.id}`}>
                      {tx(lang, "Contribution", "Aporte", "Aporte")}{" "}
                      {formatMoney(it.contribution_amount, invCur)}{" "}
                      / {(CONTRIB_FREQS.find((f) => f.v === it.contribution_frequency) || {})[tx(lang, "en", "es", "es")]?.toLowerCase()}
                    </div>
                  )}
                  {it.notes && (
                    <div
                      data-testid={`inv-notes-${it.id}`}
                      className="text-[11px] text-slate-500 mt-0.5 italic truncate max-w-xs"
                      title={it.notes}
                    >
                      “{it.notes}”
                    </div>
                  )}
                  {!isWithdrawn && it.projection?.annual_rate_pct > 0 && it.projection?.months_to_withdrawal > 0 && (
                    <div
                      data-testid={`inv-projection-${it.id}`}
                      className="text-[11px] text-sky-700 mt-0.5 tabular-nums"
                    >
                      {tx(lang, "Projected ", "Proyectado ", "Proyectado ")}
                      {formatMoney(it.projection.projected_value, it.currency || currency)}
                      {" "}({it.projection.projected_gain_pct >= 0 ? "+" : ""}{it.projection.projected_gain_pct.toFixed(1)}%)
                      {" "}{tx(lang, "in", "en", "en")} {it.projection.months_to_withdrawal.toFixed(0)} {tx(lang, "months", "meses", "meses")}
                    </div>
                  )}
                  {/* Expected-vs-actual delta — only on withdrawn rows.
                      Colored amber when the entity under-paid (delta < 0),
                      green when they over-paid, grey when matched exactly. */}
                  {delta != null && (
                    <div
                      data-testid={`inv-delta-${it.id}`}
                      className={`text-[11px] mt-0.5 tabular-nums font-medium ${
                        delta < -0.5 ? "text-amber-700" : delta > 0.5 ? "text-emerald-700" : "text-slate-500"
                      }`}
                      title={tx(lang, "Difference between Lumfi's projection and what the entity actually paid", "Diferencia entre lo proyectado por Lumfi y lo realmente cobrado de la entidad", "Diferencia entre lo proyectado por Lumfi y lo realmente cobrado de la entidad")}
                    >
                      {tx(lang, "Expected ", "Esperado ", "Esperado ")}
                      {formatMoney(expected, currency)}
                      {" → "}
                      {tx(lang, "Collected ", "Cobrado ", "Cobrado ")}
                      {formatMoney(actual, currency)}
                      {" "}
                      <span className="ml-1">
                        ({delta >= 0 ? "+" : ""}{formatMoney(delta, currency)}{deltaPct != null && `, ${deltaPct >= 0 ? "+" : ""}${deltaPct.toFixed(1)}%`})
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <MoneyAmount
                  amount={fxConvert(Number(it.current_value || 0), it.currency || currency, currency)}
                  currency={currency}
                  original_currency={it.currency && it.currency !== currency ? it.currency : null}
                  original_amount={it.currency && it.currency !== currency ? it.current_value : null}
                  fx_rate={it.fx_rate}
                  fx_at={it.fx_at}
                  size="md"
                  showFxLine
                  testid={`inv-value-${it.id}`}
                />
                {!isWithdrawn && (
                  <div className={`text-xs font-semibold tabular-nums ${itemPnl >= 0 ? "text-emerald-700" : "text-red-600"}`} data-testid={`inv-pnl-${it.id}`}>
                    {itemPnl >= 0 ? <TrendingUp className="h-3 w-3 inline" /> : <TrendingDown className="h-3 w-3 inline" />}
                    {" "}{itemPnl >= 0 ? "+" : ""}{invCur !== currency ? "≈ " : ""}{formatMoney(itemPnl, currency)}
                  </div>
                )}
                {!isWithdrawn && !it.withdrawal_date && (
                  // No maturity date → user uses the manual "Cobrar" button.
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openCollect(it)}
                    data-testid={`collect-inv-${it.id}`}
                    className="gap-1.5 text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                  >
                    <ArrowDownCircle className="h-3.5 w-3.5" />
                    {tx(lang, "Collect", "Cobrar", "Cobrar")}
                  </Button>
                )}
                {!isWithdrawn && (
                  <Button size="icon" variant="ghost" onClick={() => openEdit(it)} data-testid={`edit-inv-${it.id}`}><Pencil className="h-4 w-4" /></Button>
                )}
                <Button size="icon" variant="ghost" onClick={() => setDeleteTarget(it)} data-testid={`delete-inv-${it.id}`}><Trash2 className="h-4 w-4 text-red-600" /></Button>
              </div>
            </HighlightCard>
          );
        })}
      </div>

      <InvestmentFormDialog
        open={open}
        onOpenChange={setOpen}
        editing={editing}
        form={form}
        setForm={setForm}
        currency={currency}
        fxList={fxList}
        cashAccounts={cashAccounts}
        onSave={save}
        lang={lang}
        t={t}
        kinds={KINDS}
        contribFreqs={CONTRIB_FREQS}
        fieldErrors={fieldErrors}
        setFieldErrors={setFieldErrors}
      />

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("confirm_delete")}</AlertDialogTitle>
            <AlertDialogDescription>{deleteTarget?.name}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction data-testid="inv-delete-confirm" onClick={remove} className="bg-red-600 hover:bg-red-700">{t("delete")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <InvestmentCollectDialog
        target={collectTarget}
        onOpenChange={(o) => !o && setCollectTarget(null)}
        amount={collectAmount}
        setAmount={setCollectAmount}
        account={collectAccount}
        setAccount={setCollectAccount}
        note={collectNote}
        setNote={setCollectNote}
        currency={currency}
        cashAccounts={cashAccounts}
        onSubmit={submitCollect}
        lang={lang}
        t={t}
      />
    </Layout>
  );
}
